import moment from 'moment-timezone';
import { CrewProductivityResponse } from './types';
import { getWidgetDateOptions, WidgetDateOption } from './Widgets/components/DateSelect';
import { AssetUtilizationEquipmentType } from './Widgets/AssetUtilization';
import { ISOWeekDay } from 'utils/getStartAndEndOfWeek';

export const ANALYTICS_CONSTANTS = {
  EQUIPMENT_TYPES: [
    { label: 'Equipment', value: 'equipment' },
    { label: 'Vehicle', value: 'vehicle' }
  ],
  EQUIPMENT_VALUE: {
    EQUIPMENT: 'equipment' as const,
    VEHICLE: 'vehicle' as const
  }
};

/**
 * Returns the API refresh interval for dashboard widgets based on the date range including the current date.
 * @param startDate - The start date of the date range.
 * @param endDate - The end date of the date range.
 * @param refreshInterval - The refresh interval in milliseconds. Defaults to 15 minutes.
 * @returns The refresh interval in milliseconds.
 */
export const getWidgetRefetchInterval = (
  startDate?: Date,
  endDate?: Date,
  refreshInterval: number = 1000 * 60 * 15 // 15 minutes
) => {
  if (!startDate || !endDate) {
    return refreshInterval;
  }

  const dateRangeIncludesCurrentDate = moment().isBetween(moment(startDate), moment(endDate));

  if (dateRangeIncludesCurrentDate) {
    return refreshInterval;
  }

  return undefined;
};

export interface CrewProductivityWidgetData {
  id:
    | 'ONSITE_WORKING'
    | 'OFFSITE_WORKING'
    | 'ONSITE_TRANSPORT'
    | 'OFFSITE_TRANSPORT'
    | 'ONSITE_IDLE'
    | 'OFFSITE_IDLE'
    | 'ONSITE_UNKOWN';
  label: string;
  durationInSeconds: number;
  color: string;
}

/**
 * Returns the crew productivity widget data.
 * @param data - The crew productivity data.
 * @returns The crew productivity widget data.
 */
export const getCrewProductivityWidgetData = (
  data: CrewProductivityResponse[] | undefined
): CrewProductivityWidgetData[] => {
  if (!data) return [];

  const CREW_PRODUCTIVITY_DATA = [
    {
      id: 'ONSITE_WORKING',
      label: 'On-site Working',
      durationInSeconds: 0,
      color: '#CC0000'
    },
    {
      id: 'OFFSITE_WORKING',
      label: 'Off-site Working',
      durationInSeconds: 0,
      color: '#9C0303'
    },
    {
      id: 'TRANSPORT',
      label: 'Driving/Transport',
      durationInSeconds: 0,
      color: '#666666'
    },
    {
      id: 'ONSITE_TRANSPORT',
      label: 'On-site Transport',
      durationInSeconds: 0,
      color: '#292929'
    },
    {
      id: 'ONSITE_IDLE',
      label: 'On-site Idle',
      durationInSeconds: 0,
      color: '#F8F8F8'
    },
    {
      id: 'OFFSITE_IDLE',
      label: 'Off-site Idle',
      durationInSeconds: 0,
      color: '#E3E3E3'
    },
    {
      id: 'ONSITE_UNKOWN',
      label: 'On-site Ignition Off',
      durationInSeconds: 0,
      color: '#999999'
    }
  ];

  const availableData = CREW_PRODUCTIVITY_DATA.map(item => {
    const matchingData = data.find(d => d.BucketName === item.id);

    if (matchingData) {
      return {
        ...item,
        durationInSeconds: matchingData.TotalDuration
      };
    }

    return item;
  });

  return availableData as CrewProductivityWidgetData[];
};

interface WidgetFilters {
  dateRange?: WidgetDateOption;
  equipmentType?: AssetUtilizationEquipmentType;
}

type WidgetType =
  | 'crewProductivity'
  | 'assetUtilization'
  | 'workStatus'
  | 'maintenanceDue'
  | 'materialUsageAndTracking'
  | 'revenueAndGrossProfit';

/**
 * Persists the widget filters in the local storage.
 * @param type - The type of the widget.
 * @param filters - The filters to persist.
 */
export const persistWidgetFilters = (type: WidgetType, filters: WidgetFilters): void => {
  if (!type || !filters) {
    return;
  }

  const key = type + 'Filters';

  const filterObject = {
    // If the filters already exist, merge them with the new filters
    ...getPersistedWidgetFilters(type)
  };

  if (filters.dateRange) {
    filterObject.dateRange = filters.dateRange;
  }

  if (filters.equipmentType) {
    filterObject.equipmentType = filters.equipmentType;
  }

  localStorage.setItem(key, JSON.stringify(filterObject));
};

/**
 * Returns the persisted widget filters from the local storage.
 * @param type - The type of the widget.
 * @param widgetDateOptions - The date options for the widget.
 * @returns The persisted widget filters or null if the filters do not exist.
 * @description If the date range is custom, the date range is returned as is.
 * Otherwise, the date range is fetched relative to the current date.
 */
export const getPersistedWidgetFilters = (
  type: WidgetType,
  widgetDateOptions?: WidgetDateOption[],
  startOfWeek?: ISOWeekDay
): WidgetFilters | null => {
  if (!type) {
    return null;
  }

  const key = type + 'Filters';

  const persistedFilters = localStorage.getItem(key);
  const parsedPersistedFilters: WidgetFilters | null = persistedFilters
    ? JSON.parse(persistedFilters)
    : null;

  if (!parsedPersistedFilters) {
    return null;
  }

  if (parsedPersistedFilters?.dateRange?.isCustom) {
    return parsedPersistedFilters;
  }

  const dateRangeOptions = widgetDateOptions || getWidgetDateOptions({ startOfWeek });

  const matchingDateRange = dateRangeOptions?.find(
    option => option.label === parsedPersistedFilters?.dateRange?.label
  );

  return { ...parsedPersistedFilters, dateRange: matchingDateRange };
};

/**
 * Removes all persisted widget filters from local storage.
 */
export const removeAllWidgetFilters = (): void => {
  const widgetTypes: WidgetType[] = [
    'crewProductivity',
    'assetUtilization',
    'workStatus',
    'maintenanceDue',
    'materialUsageAndTracking',
    'revenueAndGrossProfit'
  ];

  widgetTypes.forEach(type => {
    const key = type + 'Filters';
    localStorage.removeItem(key);
  });
};
